import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient, { API_BASE_URL } from '../lib/api';
import { getSessionId } from '../lib/session';

const PdfContext = createContext();

export const usePdf = () => {
  const context = useContext(PdfContext);
  if (!context) {
    throw new Error('usePdf must be used within a PdfProvider');
  }
  return context;
};

export const PdfProvider = ({ children }) => {
  const [sessionId] = useState(getSessionId);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load PDFs on mount
  useEffect(() => {
    loadPdfs();
  }, [sessionId]);

  const loadPdfs = async () => {
    try {
      const params = { session_id: sessionId };
      const response = await apiClient.get(`${API_BASE_URL}/api/pdf/list`, { params });
      setPdfs(response.data);
    } catch (error) {
      console.error('Failed to load PDFs:', error);
      setPdfs([]);
    }
  };

  const uploadPdf = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('session_id', sessionId);

      const response = await apiClient.post(`${API_BASE_URL}/api/pdf/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const newPdf = response.data;
      setPdfs(prev => [newPdf, ...prev]);
      return newPdf;
    } finally {
      setLoading(false);
    }
  };

  const deletePdf = async (pdfId) => {
    setLoading(true);
    try {
      const url = new URL(`${API_BASE_URL}/api/pdf/${pdfId}`);
      url.searchParams.append('session_id', sessionId);
      
      await apiClient.delete(url.toString());
      setPdfs(prev => prev.filter(pdf => pdf.id !== pdfId));
    } finally {
      setLoading(false);
    }
  };

  const splitPdf = async (pdfId, splitOptions) => {
    setLoading(true);
    try {
      const payload = {
        pdf_id: pdfId,
        type: splitOptions.type,
        ...(splitOptions.type === 'pages' && { pages: splitOptions.pages }),
        ...(splitOptions.type === 'range' && { 
          start_page: splitOptions.startPage, 
          end_page: splitOptions.endPage 
        })
      };

      const url = new URL(`${API_BASE_URL}/api/pdf/split`);
      url.searchParams.append('session_id', sessionId);
      
      const response = await apiClient.post(url.toString(), payload);
      return response.data;
    } finally {
      setLoading(false);
    }
  };

  const mergePdfs = async (pdfIds) => {
    setLoading(true);
    try {
      const url = new URL(`${API_BASE_URL}/api/pdf/merge`);
      url.searchParams.append('session_id', sessionId);
      
      const response = await apiClient.post(url.toString(), {
        pdf_ids: pdfIds
      });
      return response.data;
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (fileId) => {
    const downloadUrl = `${API_BASE_URL}/api/pdf/download/${fileId}`;
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.click();
  };

  const value = {
    pdfs,
    uploadPdf,
    deletePdf,
    splitPdf,
    mergePdfs,
    downloadFile,
    loading,
    refreshPdfs: loadPdfs
  };

  return (
    <PdfContext.Provider value={value}>
      {children}
    </PdfContext.Provider>
  );
};
